import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import type { IOrganisation } from '../../interfaces';
import type { IAccount } from '../../interfaces/Account/IAccount.ts';
import AccountService from '../../services/AccountService.ts';

type OrganisationPageKeys = {
  organisationQueryKey: readonly ['organisation', string | undefined];
  organisationCockpitUsersQueryKey: readonly [
    'organisationCockpitUsers',
    string | undefined,
  ];
};

type OrganisationPageMutationCallbacks = {
  onShowResendInfo: () => void;
};

export function useOrganisationPageMutations(
  keys: OrganisationPageKeys,
  callbacks: OrganisationPageMutationCallbacks
) {
  const queryClient = useQueryClient();

  const updateAccountResendLink = (accountId: string) => {
    queryClient.setQueryData<IAccount[]>(
      keys.organisationCockpitUsersQueryKey,
      (prev) => {
        if (!prev) return prev;
        return produce(prev, (draft) => {
          const account = draft.find((a) => a.id === accountId);
          if (account) {
            account.updated_at = new Date().toISOString();
          }
        });
      }
    );
  };

  const resendInviteMutation = useMutation({
    mutationFn: (accountId: string) =>
      AccountService.initiateResetPassword(accountId),
    onSuccess: (_data, accountId) => {
      callbacks.onShowResendInfo();
      updateAccountResendLink(accountId);
    },
  });

  const setOrganisationCache = (updated: IOrganisation) => {
    queryClient.setQueryData(keys.organisationQueryKey, updated);
  };

  const invalidateOrganisationCockpitUsers = async () => {
    await queryClient.invalidateQueries({
      queryKey: keys.organisationCockpitUsersQueryKey,
    });
  };

  return {
    resendInviteMutation,
    setOrganisationCache,
    invalidateOrganisationCockpitUsers,
    isCrudLoading: resendInviteMutation.isPending,
  };
}

